import { SearchableSelect } from "../components/ui/searchable-select";
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowLeft, Plus, Edit, Trash2, User as UserIcon, Calendar, ClipboardList,
  Link2, Monitor, Clock, ExternalLink, Archive, ArchiveRestore, AlertCircle, ArrowUp, ArrowDown, Download, AlertTriangle, Building2, Copy, FileText,
  MoreVertical, ChevronDown, QrCode
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { MultiSelect } from "../components/ui/multi-select";
import { ProcedureLink, ActivityLogEntry, Procedure, Activity, User, System, PersonType, ActivityRaci, RaciRole, PartyType, RaciParty } from "../types/index";
import { canUserEditProcedure, canUserArchiveProcedure, canArchiveProcedure, canDeleteProcedure, getTrainingPlansUsingProcedure, userHasAccessToDepartment, canUserViewProcedure } from "../utils/procedure-permissions";
import jsPDF from 'jspdf';
import { debounce } from 'lodash';
import CustomEditor from "../components/ui/custom-text-editor";
import FileAttachments from "../components/FileAttachments";
import ProcedureQRModal from "../components/Procedures/ProcedureQRModal";
import { ProcedurePageSkeleton } from "../components/Procedures/ProcedurePageSkeleton";
import SharePointVideoPlayer from "../components/Procedures/SharePointVideoPlayer";
import { isArrayOfStrings, formatArchiveDate, getDepartmentIdByUser, toSydneyDate } from "../utils/helpers";
import { Can } from "../components/Can";
import { CanAny } from "../components/CanAny";
import { useAbility } from '@casl/react';
import { AbilityContext } from '../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import { ActivityLog } from "../components/ActivityLog";
import api from "../utils/api";

interface ActivityUpdateResponse {
  success: boolean;
  message?: string;
  activity: Activity;
  procedure: Procedure;
  activities: Activity[];
}

interface ProcedureResponse {
  success: boolean;
  procedure: Procedure;
}

// At the top of the file, after imports
interface ExternalPartner {
  id: string;
  name: string;
  active: boolean;
}

interface NewActivity {
  activityName: string;
  description: string;
  procedure: string;
  sequence: number;
  assignedTo: string;
  status?: string;
  dependencies?: Array<{
    activity: string;
    type: string;
  }>;
  dueDate?: string | undefined;
  responsiblePerson: string;
  accountablePerson: string;
  consultedPerson?: string | undefined;
  informedPerson?: string | undefined;
  createdBy?: string;
  createdAt?: string;
}

const getProcedure = async (id: any) => {
  const data = await api.get(`/api/procedures/${id}`);
  return data.procedure || null;
}

// Create debounced function outside component
const createDebouncedUpdate = (
  updateProcedure: (id: string, updates: any, showToast: boolean) => void,
  currentUserId: string
) =>
  debounce((newDescription: string, procedureId: string, activityLogs: Array<{
    id: string;
    timestamp: string;
    user: string | User;
    changeDescription: string;
  }> = []) => {
    const newLogEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      user: currentUserId,
      changeDescription: "Updated procedure description"
    };

    // const updatedLog = activityLogs ? [...activityLogs, newLogEntry] : [newLogEntry];

    updateProcedure(procedureId, {
      description: newDescription,
      // activityLogs: updatedLog
    }, false);
  }, 1000);

export default function ProcedurePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    departments,
    users,
    procedures,
    activities,
    systems,
    updateProcedure,
    deleteProcedureLink,
    addActivity,
    updateActivity,
    deleteActivity,
    archiveProcedure,
    unarchiveProcedure,
    trainingPlans,
    jobRoles,
    externalPartners
  } = useData();
  const { currentUser } = useAuth();
  const ability = useAbility(AbilityContext);

  // State declarations - keep all useState hooks together at the top
  const [procedure, setProcedure] = useState<Procedure | null>(null);
  const [hasProcedureLoaded, setHasProcedureLoaded] = useState<boolean>(false);
  const [description, setDescription] = useState("");
  const [procedureActivities, setProcedureActivities] = useState<Activity[]>([]);
  const [openAddActivity, setOpenAddActivity] = useState(false);
  const [openEditActivity, setOpenEditActivity] = useState(false);
  const [openDeleteActivity, setOpenDeleteActivity] = useState(false);
  const [editingActivity, setEditingActivity] = useState<{
    id: string;
    activityName: string;
    // responsiblePerson: string;
    // accountablePerson: string;
    // consultedPerson: string;
    // informedPerson: string;
    // _id: string;
    raci: ActivityRaci | undefined;
  } | null>(null);
  const [deleteActivityId, setDeleteActivityId] = useState("");
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [editSystems, setEditSystems] = useState(false);
  const [openAddLink, setOpenAddLink] = useState(false);
  const [newLink, setNewLink] = useState<{ title: string; url: string; description: string }>({
    title: "",
    url: "",
    description: ""
  });
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [openUnarchiveDialog, setOpenUnarchiveDialog] = useState(false);
  const [openEditLink, setOpenEditLink] = useState(false);
  const [editingLink, setEditingLink] = useState<ProcedureLink | null>(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = useState("");
  const [editingHowTo, setEditingHowTo] = useState(false);
  const [howToContent, setHowToContent] = useState("");
  const [newActivity, setNewActivity] = useState<NewActivity>({
    activityName: "",
    description: "",
    procedure: "",
    sequence: 0,
    assignedTo: "",
    responsiblePerson: "",
    accountablePerson: "",
    consultedPerson: undefined,
    informedPerson: undefined
  });
  const [editingDescription, setEditingDescription] = useState(false);
  const [whyImportant, setWhyImportant] = useState("");
  const [editingWhyImportant, setEditingWhyImportant] = useState(false);
  const [procedureAttachments, setProcedureAttachments] = useState<any[]>([]);
  const [saveSystemText, setSaveSystemText] = useState<string>("Save systems");
  const [isSavingSystems, setIsSavingSystems] = useState<boolean>(false);
  const [raci, setRaci] = useState<ActivityRaci | undefined>({});
  const [newActivityName, setNewActivityName] = useState("");
  const [editActivity, setEditActivity] = useState<Activity | null>(null);
  const [isDeletingActivity, setIsDeletingActivity] = useState(false);
  const [activeTab, setActiveTab] = useState<'procedure' | 'activity_log'>('procedure');
  const [activityLogs, setActivityLogs] = useState<ActivityLogEntry[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  // Refs
  const debouncedUpdateRef = useRef<ReturnType<typeof createDebouncedUpdate>>();

  // Fetch activity logs
  const fetchActivityLogs = async () => {
    if (!id) return;

    setIsLoadingLogs(true);
    try {
      const data = await api.get(`/api/procedures/${id}/activity-logs`);
      if (data.success) {
        setActivityLogs(data.activityLogs);
      }
    } catch (error) {
      console.error("Error fetching activity logs:", error);
      toast.error("Failed to load activity logs");
    } finally {
      setIsLoadingLogs(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'activity_log') {
      fetchActivityLogs();
    }
  }, [activeTab, id]);

  // Computed values
  const canEdit = currentUser?.userRole === "Admin" ||
    currentUser?.userRole === "Director" ||
    (currentUser?.userRole === "Manager" && procedure?.department?.id == getDepartmentIdByUser(currentUser)) ||
    (currentUser?.jobRole === "HSEQ Manager");

  const canDelete = currentUser?.userRole === "Admin" ||
    currentUser?.userRole === "Director" ||
    currentUser?.jobRole === "HSEQ Manager";

  const canArchive = currentUser && procedure ? canUserArchiveProcedure(currentUser, procedure) : false;

  // Effects - keep all useEffect hooks together
  useEffect(() => {
    if (editingActivity?.raci) {
      setRaci(editingActivity.raci);
    } else {
      setRaci({}); // Reset if there's no activity
    }
  }, [editingActivity]);

  useEffect(() => {
    if (procedure?.whyImportant !== undefined) {
      setWhyImportant(procedure.whyImportant);
    }
  }, [procedure?.whyImportant]);

  useEffect(() => {
    if (procedure?.howToComplete !== undefined) {
      setHowToContent(procedure.howToComplete);
    }
  }, [procedure?.howToComplete]);

  useEffect(() => {
    if (procedure?.description) {
      setDescription(procedure.description);
    }
  }, [procedure?.description]);

  useEffect(() => {

    const fetchProcedure = async () => {
      if (id) {
        setHasProcedureLoaded(false);
        try {
          const foundProcedure = await getProcedure(id);
          if (foundProcedure) {
            setProcedure(foundProcedure);
            setDescription(foundProcedure.description || "");
            setSelectedSystems(foundProcedure.systems?.map((s: System) => typeof s !== 'string' ? s.name : "") || []);
            setProcedureAttachments(foundProcedure.attachments || []);
            const relatedActivities = foundProcedure?.activities;
            setProcedureActivities(relatedActivities);
          }
        } catch (error) {
          console.error("Error fetching procedure:", error);
        } finally {
          setHasProcedureLoaded(true);
        }
      }
    }

    fetchProcedure();
  }, [id, procedures]);

  useEffect(() => {
    if (procedure?.id) {
      setNewActivity(prev => ({
        ...prev,
        procedure: procedure.id
      }));
    }
  }, [procedure?.id]);

  const stakeholders = useMemo(() => {
    return procedure?.procedureStakeholders
      ? procedure.procedureStakeholders
        .map(stakeholder => typeof stakeholder === 'string' ? users.find(u => u.id === stakeholder) : stakeholder)
        .filter((user): user is User => !!user)
      : [];
  }, [procedure?.procedureStakeholders, users]);

  const viewers = useMemo(() => {
    return procedure?.viewers
      ? procedure.viewers
        .map(viewer => typeof viewer === 'string' ? users.find(u => u.id === viewer) : viewer)
        .filter((user): user is User => !!user)
      : [];
  }, [procedure?.viewers, users]);

  const allSystemsFormatted = useMemo(() => {
    return systems
      ?.map(system => ({
        value: system.name,
        label: system.active ? system.name : `${system.name} (Inactive)`,
        isInactive: !system.active
      }))
      .sort((a, b) => {
        if (a.isInactive !== b.isInactive) return a.isInactive ? 1 : -1;
        return a.label.localeCompare(b.label);
      }) || [];
  }, [systems]);
  // Add this useEffect after the other useEffects

  // Callbacks
  // const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
  //   if (!procedure || !debouncedUpdateRef.current) return;
  //   const newDescription = e.target.value;
  //   setDescription(newDescription);
  //   debouncedUpdateRef.current(newDescription, procedure.id, procedure.activityLogs);
  // }, [procedure]);

  const getPartyName = (party: RaciParty): string => {
    if (!party || !party.id) return "Invalid Party";

    switch (party.type) {
      case PartyType.User: {
        const user = users.find(u => u.id == party.id);
        if (user) return user.name;
        // Fallback: if the party object itself has a name (though rare in this app's RACI JSON)
        if ('name' in party && (party as any).name) return (party as any).name;
        return "Unknown User";
      }
      case PartyType.JobRole: {
        const role = jobRoles.find(r => r.id == party.id);
        return role ? `${role.title} (Role)` : "Unknown Role";
      }
      case PartyType.ExternalPartner: {
        const partner = externalPartners.find(p => p.id == party.id);
        return partner ? `${partner.name} (External)` : "Unknown Partner";
      }
      default:
        return "Unknown Party Type";
    }
  };


  // Add this sorting function before the useEffect
  const sortActivities = (activities: Activity[]): Activity[] => {
    return [...activities]
      .filter(activity => activity && activity.id)
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  };

  if (!hasProcedureLoaded) {
    return <ProcedurePageSkeleton />
  }

  if (!procedure) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>Procedure not found</p>
      </div>
    );
  }

  if (!canUserViewProcedure(currentUser, procedure, ability)) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>You don't have permission to access this procedure</p>
      </div>
    );
  }

  // Update how we get the owner
  const owner = typeof procedure.procedureOwner === 'string'
    ? users.find(user => user.id === procedure.procedureOwner)
    : procedure.procedureOwner;

  // const department = departments.find(dept => dept.departmentCode == procedure.departmentId);
  const department = procedure.department;
  const creator = typeof procedure.createdBy === 'string'
    ? users.find(user => user.id === procedure.createdBy)
    : procedure.createdBy;
  const archiver = procedure.archivedBy ? (
    typeof procedure.archivedBy === 'string'
      ? users.find(user => user.id == procedure.archivedBy)
      : procedure.archivedBy
  ) : null;

  // Helper function to get user ID
  const getUserId = (userOrId: string | User | undefined | null): string => {
    if (!userOrId) return "";
    if (userOrId === "none") return "none";
    return typeof userOrId === 'string' ? userOrId : userOrId.id;
  };

  // Helper function to get user name
  const getUserName = (userId: string | User | null | undefined): string => {
    if (!userId) return "Not assigned";
    if (userId === "none") return "None";

    if (typeof userId === 'string') {
      // Check if it's a user
      const user = users.find(u => u.id === userId || u.name === userId);
      if (user) return user.name;

      // Check if it's a job role
      const role = jobRoles.find(r => r.id === userId);
      if (role) return `${role.title} (Role)`;

      // Check if it's an external partner
      const partner = externalPartners.find(p => p.id === userId);
      if (partner) return `${partner.name} (External)`;

      return "Unknown User";
    }

    return userId.name || "Unknown User";
  };

  const handleSystemsUpdate = async () => {
    if (!procedure) return;

    setSaveSystemText("Saving systems...");
    setIsSavingSystems(true);

    const newLogEntry: ActivityLogEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      user: currentUser?.id || "",
      changeDescription: "Updated systems used"
    };

    // const updatedLog = procedure.activityLogs ? [...procedure.activityLogs, newLogEntry] : [newLogEntry];

    // Make the API call to update the backend
    await updateProcedure(procedure.id, {
      systems: selectedSystems,
      // activityLogs: updatedLog
    }, true);

    // Update local state
    setProcedure((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        systems: selectedSystems,
        // activityLogs: updatedLog
      };
    });

    // Close edit mode
    setSaveSystemText("Save systems");
    setIsSavingSystems(false);
    setEditSystems(false);
  };

  const handleAddLink = () => {
    if (!newLink.title || !newLink.url) {
      toast.error("Link title and URL are required");
      return;
    }

    const newLinkEntry: ProcedureLink = {
      id: Date.now().toString(),
      title: newLink.title,
      url: newLink.url,
      description: newLink.description
    };

    // Add to activity log
    const newLogEntry: ActivityLogEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      user: currentUser?.id || "",
      changeDescription: `Added link: ${newLink.title}`
    };

    const updatedLinks = procedure.links ? [...procedure.links, newLinkEntry] : [newLinkEntry];
    // const updatedLog = procedure.activityLogs ? [...procedure.activityLogs, newLogEntry] : [newLogEntry];

    // Make a single update call with both links and activityLog
    updateProcedure(procedure.id, {
      links: updatedLinks.map(link => ({
        id: link.id,
        title: link.title,
        url: link.url,
        description: link.description || ""
      })),
      // activityLogs: updatedLog
    }, true);

    // Update local state
    setProcedure((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        links: updatedLinks.map(link => ({
          id: link.id,
          title: link.title,
          url: link.url,
          description: link.description || ""
        })),
        // activityLogs: updatedLog
      };
    });

    setNewLink({ title: "", url: "", description: "" });
    setOpenAddLink(false);
  };

  const handleRemoveLink = (linkId: string) => {
    if (!procedure) return;

    const linkToDelete = procedure.links?.find((link: ProcedureLink) => link.id == linkId);
    const updatedLinks = procedure.links?.filter((link: ProcedureLink) => link.id != linkId) || [];

    const newLogEntry: ActivityLogEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      user: currentUser !== null ? currentUser : "",
      changeDescription: `Removed link: ${linkToDelete?.title || "Unknown"}`
    };

    // const updatedLog = procedure.activityLogs ? [...procedure.activityLogs, newLogEntry] : [newLogEntry];

    deleteProcedureLink(procedure.id, linkId);
    updateProcedure(procedure.id, {}, false);

    // Update local state
    setProcedure((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        links: updatedLinks.map(link => ({
          id: link.id,
          title: link.title,
          url: link.url,
          description: link.description || "" // Ensure description is always a string
        })),
        // activityLogs: updatedLog
      };
    });
  };

  const handleAddActivity = async () => {
    // 1. Validate Input
    if (!newActivityName) {
      toast.error("Activity name is required.");
      return;
    }
    if (!raci?.responsiblePerson || !raci.accountablePerson) {
      toast.error("A Responsible and an Accountable person/role are required.");
      return;
    }
    if (!procedure?.id) {
      toast.error("Procedure context is missing. Please refresh the page.");
      return;
    }

    // 2. Construct the Payload
    // Calculate the sequence for the new activity to place it at the end.
    const sequence = procedureActivities.length > 0
      ? Math.max(...procedureActivities.map(a => a.sequence || 0)) + 1
      : 0;

    const newActivityPayload = {
      activityName: newActivityName,
      raci: raci,
      procedureId: procedure.id,
      sequence: sequence,
    };

    // 3. Call the Context Function
    try {
      const createdActivity = await addActivity(newActivityPayload);

      if (createdActivity) {
        // 4. Handle Success
        toast.success("Activity added successfully!");

        // The response should contain the updated list of all activities for the procedure.
        // Update the local state to re-render the table.
        setProcedureActivities(prevActivities => {
          const newActivity = {
            ...createdActivity,
            procedure: procedure.id,
            raci
          };
          const newActivities = [...prevActivities, newActivity];
          console.log('[ProcedurePage] Updated activities:', newActivities);
          return newActivities;
        });

        // Close the dialog (the onOpenChange handler will reset the state).
        setOpenAddActivity(false);

        setRaci({});
        setNewActivityName('');

      } else {
        // Handle backend error
        toast.error("Failed to add activity.");
      }
    } catch (error) {
      // 5. Handle Unexpected Errors
      console.error("[ProcedurePage] Error adding activity:", error);
      toast.error("An unexpected error occurred while adding the activity.");
    }
  };

  const handleRaciSelect = (role: RaciRole, value: string | string[]) => {
    // Check if the role is one that accepts multiple parties (an array)
    if (role === 'consultedPerson' || role === 'informedPerson') {
      // Ensure the value is an array, as expected from a multi-select component
      if (Array.isArray(value)) {
        const parties: RaciParty[] = value.map(v => {
          const [type, id] = v.split('-');
          return { id, type: type as PartyType };
        });

        setRaci(currentRaci => ({
          ...currentRaci,
          [role]: parties,
        }));
      }
      return; // End execution for multi-select roles
    }

    // Handle roles that accept a single party (a string value)
    if (typeof value === 'string') {
      const [type, id] = value.split('-');

      // If "None" is selected, remove the property from the RACI object
      if (id === 'none') {
        setRaci(currentRaci => {
          const { [role]: removed, ...rest } = currentRaci || {};
          return rest;
        });
        return;
      }

      // Otherwise, create the RaciParty object for the single selection
      const newParty: RaciParty = {
        id: id,
        type: type as PartyType,
      };

      setRaci(currentRaci => ({
        ...currentRaci,
        [role]: newParty,
      }));
    }
  };

  const handleUpdateActivity = async () => {
    if (!editingActivity || !editingActivity.activityName || !raci?.responsiblePerson || !raci?.accountablePerson) {
      toast.error("Activity name, responsible person, and accountable person are required");
      return;
    }

    if (!procedure?.id) {
      toast.error("Procedure not found. Please refresh the page.");
      return;
    }

    try {
      // Prepare the activity update object
      const activityUpdate = {
        activityName: editingActivity.activityName,
        // responsiblePerson: editingActivity.responsiblePerson,
        // accountablePerson: editingActivity.accountablePerson,
        // consultedPerson: editingActivity.consultedPerson === "none" ? null : editingActivity.consultedPerson,
        // informedPerson: editingActivity.informedPerson === "none" ? null : editingActivity.informedPerson,
        raci
      };

      // Update the activity
      const response = await updateActivity(procedure.id, editingActivity.id, activityUpdate);

      if (response.success) {
        // Update local state
        setProcedureActivities(response.activities);
        toast.success("Activity updated successfully");
      }

      setEditingActivity(null);
      setOpenEditActivity(false);
    } catch (error) {
      // Only show error if it's not a token expiration
      if (!(error instanceof Error && error.message === "Token expired")) {
        console.error("[ProcedurePage] Error updating activity:", error);
        toast.error("Failed to update activity");
      }
    }
  };

  const setSelectedPerson = (prop: string, value: string) => {
    const [type, id] = value.split("-");
    setRaci({ ...raci, [prop]: { id, type: PersonType[type as keyof typeof PersonType] } });
  }

  const handleDeleteActivity = async () => {
    setIsDeletingActivity(true);
    try {
      // Find the activity being deleted
      const activityToDelete = procedureActivities.find(a => a.id == deleteActivityId);
      if (!activityToDelete) return;

      // Create an activity log entry
      const newLogEntry: ActivityLogEntry = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        user: currentUser?.id || "",
        changeDescription: `Activity "${activityToDelete.activityName}" deleted`
      };

      // Update procedure with new activity log
      // const updatedLog = procedure.activityLogs ? [...procedure.activityLogs, newLogEntry] : [newLogEntry];
      await updateProcedure(procedure.id, {}, false);

      // Delete the activity
      await deleteActivity(procedure.id, deleteActivityId);

      // Update local state
      setProcedureActivities(prev => prev.filter(a => a.id !== deleteActivityId));

      setDeleteActivityId("");
      setOpenDeleteActivity(false);
      toast.success("Activity deleted successfully");
    } catch (error) {
      console.error("Error deleting activity:", error);
      toast.error("Failed to delete activity");
    } finally {
      setIsDeletingActivity(false);
    }
  };

  const moveActivityUp = async (currentIndex: number) => {
    if (currentIndex === 0) return; // Already at the top

    try {
      const newActivities = [...procedureActivities];
      const currentActivity = newActivities[currentIndex];
      const previousActivity = newActivities[currentIndex - 1];

      if (!currentActivity || !previousActivity) return;

      // Optimistically update the UI
      const optimisticActivities = [...newActivities];
      [optimisticActivities[currentIndex], optimisticActivities[currentIndex - 1]] =
        [optimisticActivities[currentIndex - 1], optimisticActivities[currentIndex]];

      // Update sequences
      const tempSeq = optimisticActivities[currentIndex].sequence;
      optimisticActivities[currentIndex].sequence = optimisticActivities[currentIndex - 1].sequence;
      optimisticActivities[currentIndex - 1].sequence = tempSeq;

      setProcedureActivities(optimisticActivities);

      // Make the API call
      const response = await updateActivity(procedure.id, currentActivity.id, {
        sequence: previousActivity.sequence,
        _swapWithActivityId: previousActivity.id
      }, false);

      // If the API call fails, revert to the original state
      // if (!response.success) {
      //   setProcedureActivities(newActivities);
      //   throw new Error(response.message || 'Failed to reorder activities');
      // }

      // // Update with the server response if successful
      // if (response.activities && Array.isArray(response.activities)) {
      //   setProcedureActivities(sortActivities(response.activities));
      // }
    } catch (error) {
      console.error('Error moving activity up:', error);
      toast.error('Failed to reorder activities');
    }
  };

  const moveActivityDown = async (currentIndex: number) => {
    if (currentIndex === procedureActivities.length - 1) return; // Already at the bottom

    try {
      const newActivities = [...procedureActivities];
      const currentActivity = newActivities[currentIndex];
      const nextActivity = newActivities[currentIndex + 1];

      if (!currentActivity || !nextActivity) return;

      // Optimistically update the UI
      const optimisticActivities = [...newActivities];
      [optimisticActivities[currentIndex], optimisticActivities[currentIndex + 1]] =
        [optimisticActivities[currentIndex + 1], optimisticActivities[currentIndex]];

      // Update sequences
      const tempSeq = optimisticActivities[currentIndex].sequence;
      optimisticActivities[currentIndex].sequence = optimisticActivities[currentIndex + 1].sequence;
      optimisticActivities[currentIndex + 1].sequence = tempSeq;

      setProcedureActivities(optimisticActivities);

      // Make the API call
      const response = await updateActivity(procedure.id, currentActivity.id, {
        sequence: nextActivity.sequence,
        _swapWithActivityId: nextActivity.id
      }, false);

      // If the API call fails, revert to the original state
      // if (!response.success) {
      //   setProcedureActivities(newActivities);
      //   throw new Error(response.message || 'Failed to reorder activities');
      // }

      // // Update with the server response if successful
      // if (response.activities && Array.isArray(response.activities)) {
      //   setProcedureActivities(sortActivities(response.activities));
      // }
    } catch (error) {
      console.error('Error moving activity down:', error);
      toast.error('Failed to reorder activities');
    }
  };

  // Handle archiving a procedure
  const handleArchive = () => {
    if (!currentUser || !procedure) return;

    // Get training plans using this procedure for warning message
    const usedInPlans = getTrainingPlansUsingProcedure(procedure, trainingPlans);

    // Archive the procedure
    archiveProcedure(procedure.id, archiveReason);
    setOpenArchiveDialog(false);
    setArchiveReason("");
  };

  // Handle unarchiving a procedure
  const handleUnarchive = () => {
    if (!currentUser) return;

    unarchiveProcedure(procedure.id, currentUser.id);
    setOpenUnarchiveDialog(false);

    // Update local state
    setProcedure({
      ...procedure,
      archived: false,
      archivedOn: undefined,
      archivedBy: undefined,
      archivedReason: undefined
    });
  };



  // Filter departmental users for activity assignment
  const departmentalUsers = users;

  // Add handleEditLink function
  const handleEditLink = () => {
    if (!procedure || !editingLink) return;

    const updatedLinks = procedure.links?.map((link: ProcedureLink) =>
      link.id === editingLink.id ? {
        ...editingLink,
        description: editingLink.description || ""
      } : link
    ) || [];

    // Create a new log entry for the link update
    const newLogEntry: ActivityLogEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      user: currentUser?.id || "",
      changeDescription: `Updated link: ${editingLink.title}`
    };

    // const updatedLog = procedure.activityLogs ? [...procedure.activityLogs, newLogEntry] : [newLogEntry];

    updateProcedure(procedure.id, {
      links: updatedLinks.map(link => ({
        id: link.id,
        title: link.title,
        url: link.url,
        description: link.description || ""
      })),
      // activityLogs: updatedLog
    }, true);
    setEditingLink(null);
    setOpenEditLink(false);
  };

  const getDepartmentName = (departmentId: string): string => {
    const department = departments.find(d => d.departmentCode === departmentId);
    return department ? `${department.departmentCode} - ${department.departmentName}` : "Unknown Department";
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    let yPos = 20;

    // Add title
    doc.setFontSize(20);
    doc.text(procedure.procedureName, 20, yPos);
    yPos += 15;

    // Add metadata
    doc.setFontSize(12);
    doc.text(`Created by: ${creator ? creator.name : 'Unknown'}`, 20, yPos);
    yPos += 10;
    doc.text(`Created on: ${toSydneyDate(procedure.createdAt)}`, 20, yPos);
    yPos += 10;
    doc.text(`Department: ${getDepartmentName(procedure.departmentId)}`, 20, yPos);
    yPos += 15;

    // Add Systems Used section
    if (procedure.systems && procedure.systems.length > 0) {
      doc.setFontSize(14);
      doc.text('Systems Used:', 20, yPos);
      yPos += 10;
      doc.setFontSize(10);
      !isArrayOfStrings(procedure.systems) && procedure.systems.forEach((system: System) => {
        doc.text(`• ${system.name}`, 25, yPos);
        yPos += 7;
      });
      yPos += 5;
    }

    // Add Links section
    if (procedure.links && procedure.links.length > 0) {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Links:', 20, yPos);
      yPos += 10;
      doc.setFontSize(10);
      procedure.links.forEach((link: ProcedureLink) => {
        doc.text(`• ${link.title}`, 25, yPos);
        yPos += 7;
        doc.setTextColor(0, 0, 255); // Blue color for URLs
        doc.text(`  ${link.url}`, 30, yPos);
        doc.setTextColor(0, 0, 0); // Reset to black
        yPos += 7;
        if (link.description) {
          doc.text(`  ${link.description}`, 30, yPos);
          yPos += 7;
        }
      });
      yPos += 5;
    }

    // Add description
    if (procedure.description) {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.text('Description:', 20, yPos);
      yPos += 10;
      doc.setFontSize(10);
      const splitDescription = doc.splitTextToSize(procedure.description, 170);
      doc.text(splitDescription, 20, yPos);
      yPos += splitDescription.length * 7 + 10;
    }

    // Add activities
    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.text('Activities:', 20, yPos);
    yPos += 10;

    procedureActivities.forEach((activity, index) => {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(12);
      doc.text(`${index + 1}. ${activity.activityName}`, 25, yPos);
      yPos += 10;

      // Add RACI info
      doc.setFontSize(10);
      const responsible = typeof activity.responsiblePerson !== 'string' ? activity.responsiblePerson : null;
      const accountable = typeof activity.accountablePerson !== 'string' ? activity.accountablePerson : null;
      const consulted = typeof activity.consultedPerson !== 'string' ? activity.consultedPerson : null;
      const informed = typeof activity.informedPerson !== 'string' ? activity.informedPerson : null;

      if (responsible) {
        doc.text(`Responsible: ${responsible.name}`, 30, yPos);
        yPos += 7;
      }
      if (accountable) {
        doc.text(`Accountable: ${accountable.name}`, 30, yPos);
        yPos += 7;
      }
      if (consulted && consulted.id !== "none") {
        doc.text(`Consulted: ${consulted.name}`, 30, yPos);
        yPos += 7;
      }
      if (informed && informed.id !== "none") {
        doc.text(`Informed: ${informed.name}`, 30, yPos);
        yPos += 7;
      }

      yPos += 5;
    });

    // Save the PDF
    doc.save(`${procedure.procedureName.replace(/\s+/g, '_')}.pdf`);
  };



  const handleDelete = () => {
    if (!currentUser || !procedure || deleteConfirmation !== "delete") return;

    // Check if procedure can be deleted
    const { canDelete } = canDeleteProcedure(procedure, trainingPlans);

    if (!canDelete) {
      setOpenDeleteDialog(false);
      setDeleteConfirmation("");
      return;
    }

    updateProcedure(procedure.id, {
      deleted: true,
      deletedBy: currentUser.id
    }, false); // Set showToast to false since we'll show our own toast
    setDeleteConfirmation("");
    setOpenDeleteDialog(false);
    toast.success("Procedure deleted successfully");
    navigate("/procedure");
  };



  // Add this handler function
  const handleHowToSave = async () => {
    if (!procedure) return;

    const newLogEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      user: currentUser?.id || "",
      changeDescription: "Updated how to complete instructions"
    };

    // const updatedLog = procedure.activityLogs ? [...procedure.activityLogs, newLogEntry] : [newLogEntry];

    try {
      await updateProcedure(procedure.id, {
        howToComplete: howToContent,
        // activityLogs: updatedLog
      }, true);
      setEditingHowTo(false);
      // Update local state
      setProcedure(prev => prev ? {
        ...prev,
        howToComplete: howToContent,
        // activityLogs: updatedLog
      } : null);
    } catch (error) {
      console.error('Failed to save how to complete:', error);
      toast.error('Failed to save changes');
    }
  };

  const handleDescriptionSave = async () => {
    if (!procedure) return;

    const newLogEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      user: currentUser?.id || "",
      changeDescription: "Updated procedure description"
    };

    // const updatedLog = procedure.activityLogs ? [...procedure.activityLogs, newLogEntry] : [newLogEntry];

    try {
      await updateProcedure(procedure.id, {
        description: description,
        // activityLogs: updatedLog
      }, true);
      setEditingDescription(false);
    } catch (error) {
      console.error('Failed to save description:', error);
      toast.error('Failed to save changes');
    }
  };

  const handleWhyImportantSave = async () => {
    if (!procedure) return;

    const newLogEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      user: currentUser?.id || "",
      changeDescription: "Updated why this procedure is important"
    };

    // const updatedLog = procedure.activityLogs ? [...procedure.activityLogs, newLogEntry] : [newLogEntry];

    try {
      await updateProcedure(procedure.id, {
        whyImportant: whyImportant,
        // activityLogs: updatedLog
      }, true);
      setEditingWhyImportant(false);
      // Update local state
      setProcedure(prev => prev ? {
        ...prev,
        whyImportant: whyImportant,
        // activityLogs: updatedLog
      } : null);
    } catch (error) {
      console.error('Failed to save why important:', error);
      toast.error('Failed to save changes');
    }
  };

  // Add the fetchActivities function
  // const fetchActivities = async () => {
  //   if (!procedure?.id) return;
  //   try {
  //     // Use the existing procedure state update mechanism
  //     setProcedureActivities(prevActivities => {
  //       // Filter out the old activities and add the new one
  //       return [...prevActivities];
  //     });
  //   } catch (error) {
  //     console.error('[ProcedurePage] Error fetching activities:', error);
  //   }
  // };

  // Add back the users filtering — only Manager and above
  const managerAndAboveRoles = ["Admin", "Director", "Senior Manager", "Manager"];
  const filteredUsers = users.filter(user => {
    // Only show users that are Manager level or above
    if (!managerAndAboveRoles.includes(user.userRole)) return false;

    const userDepartmentId = getDepartmentIdByUser(user);
    if (currentUser?.userRole === "Admin" || currentUser?.userRole === "Director" || currentUser?.jobRole === 'HSEQ Manager') {
      return true; // Show all manager-and-above users
    } else {
      return userDepartmentId == getDepartmentIdByUser(currentUser as User);
    }
  });

  // Refetch procedure from server to ensure proper synchronization
  const refetchProcedure = async () => {
    if (!id) return;

    try {
      setHasProcedureLoaded(false);
      const data = await api.get(`/api/procedures/${id}`);

      if (data.success && data.procedure) {
        // Update the local procedure state with fresh server data
        setProcedure(data.procedure);
        setProcedureAttachments(data.procedure.attachments || []);
      }
      setHasProcedureLoaded(true);
    } catch (error) {
      console.error('Error refetching procedure:', error);
      setHasProcedureLoaded(true);
    }
  };

  // Handle attachments change - refetch from server to ensure proper sync
  const handleAttachmentsChange = async (newAttachments: any[]) => {
    // Immediately update local state for optimistic UI
    setProcedureAttachments(newAttachments);
    if (procedure) {
      setProcedure({
        ...procedure,
        attachments: newAttachments,
      });
    }

    // Refetch from server to ensure proper synchronization
    // Small delay to allow server to process the upload
    setTimeout(() => {
      refetchProcedure();
    }, 500);
  };

  const accessibleDepartments = departments.filter(
    dept => currentUser && userHasAccessToDepartment(currentUser, dept.departmentCode)
  );

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <Button variant="outline" size="icon" className="flex-shrink-0" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="flex flex-wrap items-center gap-2 text-lg sm:text-2xl font-bold">
              <span className="truncate">{procedure.procedureName}</span>
              <Badge variant="outline" className="bg-blue-100 border-blue-200 text-blue-800 hover:bg-blue-100 flex-shrink-0 text-xs">
                Procedure
              </Badge>
              {procedure.archived && (
                <Badge variant="outline" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 flex-shrink-0 text-xs">
                  <Archive className="w-3 h-3 mr-1" /> Archived
                </Badge>
              )}
            </h1>
          </div>
        </div>

        <div className="hidden lg:flex gap-2 flex-shrink-0">
          {!procedure.archived && (
            <>
              <CanAny of={[
                { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE },
                { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE_I_OWN }
              ]}>
                <Link to={`/procedure/${procedure.id}/edit`}>
                  <Button variant="outline">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Procedure
                  </Button>
                </Link>
              </CanAny>

              <Can I={ACTIONS.DOWNLOAD} a={RESOURCES.PROCEDURE_PDF}>
                <Button variant="outline" onClick={() => handleDownloadPDF()}>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </Can>

              <Button variant="outline" onClick={() => setQrModalOpen(true)}>
                <QrCode className="w-4 h-4 mr-2" />
                QR Code
              </Button>
            </>
          )}

          {/* Archive/Unarchive buttons based on current state */}
          {!procedure.archived && (
            <Can I={ACTIONS.ARCHIVE} a={RESOURCES.PROCEDURE}>
              <Dialog
                open={openArchiveDialog}
                onOpenChange={(open) => {
                  setOpenArchiveDialog(open);
                  if (!open) {
                    setArchiveReason("");
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Archive className="w-4 h-4 mr-2" />
                    Archive
                  </Button>
                </DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle className="text-base font-medium">Archive Procedure</DialogTitle>
                  </DialogHeader>
                  <>{(() => {
                    const { canArchive, activePlans } = canArchiveProcedure(procedure, trainingPlans);

                    if (!canArchive) {
                      return (
                        <div className="space-y-4">
                          <div className="bg-[#FEF9C3] border border-[#F7E9A8] rounded-lg p-4">
                            <div className="flex gap-2">
                              <AlertTriangle className="w-5 h-5 text-[#854D0E]" />
                              <div className="text-[#854D0E] text-sm">
                                <p>This procedure cannot be archived because it's currently used in the following active (Not Started & In Progress) training plans:</p>
                                <ul className="list-disc list-inside mt-2">
                                  {activePlans.map((plan: any) => (
                                    <li key={plan.id}>
                                      {getUserName(plan.traineeId)} – {plan.trainingPlanName} – {plan.status}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Please remove this procedure from the listed training plans before attempting to archive it again.
                          </p>
                          <DialogFooter>
                            <DialogClose asChild>
                              <Button variant="outline">Close</Button>
                            </DialogClose>
                          </DialogFooter>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Are you sure you want to archive "{procedure.procedureName}"?<br />
                          This will make the procedure inactive but preserve its history.
                        </p>
                        <div className="grid gap-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="archiveReason">Reason for Archiving (Optional)</Label>
                            <Textarea
                              id="archiveReason"
                              placeholder="Enter the reason why this procedure is being archived..."
                              value={archiveReason}
                              onChange={(e) => setArchiveReason(e.target.value)}
                              className="resize-none"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleArchive();
                                }
                              }}
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="outline">Cancel</Button>
                          </DialogClose>
                          <Button onClick={handleArchive}>Archive</Button>
                        </DialogFooter>
                      </div>
                    );
                  })()}</>
                </DialogContent>
              </Dialog>
            </Can>
          )}

          {procedure.archived && (
            <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.PROCEDURE}>
              <Dialog open={openUnarchiveDialog} onOpenChange={setOpenUnarchiveDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <ArchiveRestore className="w-4 h-4 mr-2" />
                    Unarchive
                  </Button>
                </DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle>Unarchive Procedure</DialogTitle>
                    <DialogDescription>
                      This will restore the procedure to an active state.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button onClick={handleUnarchive}>
                      Unarchive
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Can>
          )}

          {/* Add Delete button */}
          {!procedure.archived && (
            <Can I={ACTIONS.DELETE} a={RESOURCES.PROCEDURE}>
              <Dialog
                open={openDeleteDialog}
                onOpenChange={(open) => {
                  if (open) {
                    setOpenDeleteDialog(true);
                    setDeleteConfirmation("");
                  } else {
                    setOpenDeleteDialog(false);
                    setDeleteConfirmation("");
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-red-200 text-red-700 hover:bg-red-100 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle>Delete Procedure</DialogTitle>
                  </DialogHeader>
                  {(() => {
                    const { canDelete } = canDeleteProcedure(procedure, trainingPlans);

                    if (!canDelete) {
                      return (
                        <div className="space-y-4 py-2">
                          <p className="text-destructive font-medium">
                            This procedure cannot be deleted due to the following:
                          </p>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-[0.925rem]">
                              <FileText className="h-4 w-4" />
                              <p>Has historical data linked to training plans</p>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            You can archive them to keep a historical record while making them inactive in the system.
                          </p>
                          <DialogFooter>
                            <DialogClose asChild>
                              <Button variant="outline">Okay</Button>
                            </DialogClose>
                          </DialogFooter>
                        </div>
                      );
                    }

                    return (
                      <>
                        <div className="py-4">
                          <p>Are you sure you want to delete the procedure "{procedure.procedureName}"?</p>
                          <p className="text-sm text-muted-foreground mt-2">Type "delete" to confirm.</p>
                          <div className="mt-4">
                            <Label htmlFor="deleteConfirmation">Confirmation</Label>
                            <Input
                              id="deleteConfirmation"
                              value={deleteConfirmation}
                              onChange={(e) => setDeleteConfirmation(e.target.value)}
                              placeholder="delete"
                              className="mt-1.5"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && deleteConfirmation === "delete") {
                                  e.preventDefault();
                                  handleDelete();
                                }
                              }}
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="outline">Cancel</Button>
                          </DialogClose>
                          <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={deleteConfirmation !== "delete"}
                          >
                            Delete
                          </Button>
                        </DialogFooter>
                      </>
                    );
                  })()}
                </DialogContent>
              </Dialog>
            </Can>
          )}
        </div>
      </div>

      {/* Mobile Actions Dropdown */}
      <div className="lg:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="w-full sm:w-auto">
              <MoreVertical className="w-4 h-4 mr-2" />
              Actions
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {!procedure.archived && (
              <>
                <CanAny of={[
                  { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE },
                  { I: ACTIONS.UPDATE, a: RESOURCES.PROCEDURE_I_OWN }
                ]}>
                  <DropdownMenuItem asChild>
                    <Link to={`/procedure/${procedure.id}/edit`} className="cursor-pointer">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Procedure
                    </Link>
                  </DropdownMenuItem>
                </CanAny>

                <Can I={ACTIONS.DOWNLOAD} a={RESOURCES.PROCEDURE_PDF}>
                  <DropdownMenuItem onClick={() => handleDownloadPDF()}>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </DropdownMenuItem>
                </Can>

                <DropdownMenuItem onClick={() => setQrModalOpen(true)}>
                  <QrCode className="w-4 h-4 mr-2" />
                  QR Code
                </DropdownMenuItem>
              </>
            )}
            {!procedure.archived && (
              <Can I={ACTIONS.ARCHIVE} a={RESOURCES.PROCEDURE}>
                <DropdownMenuItem onClick={() => setOpenArchiveDialog(true)}>
                  <Archive className="w-4 h-4 mr-2" />
                  Archive
                </DropdownMenuItem>
              </Can>
            )}
            {procedure.archived && (
              <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.PROCEDURE}>
                <DropdownMenuItem onClick={() => setOpenUnarchiveDialog(true)}>
                  <ArchiveRestore className="w-4 h-4 mr-2" />
                  Unarchive
                </DropdownMenuItem>
              </Can>
            )}
            {!procedure.archived && (
              <Can I={ACTIONS.DELETE} a={RESOURCES.PROCEDURE}>
                <DropdownMenuItem
                  onClick={() => setOpenDeleteDialog(true)}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </Can>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>


      {/* Archive warning banner */}
      {procedure.archived && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle size={20} className="text-yellow-600" />
              <div>
                <h3 className="font-medium text-yellow-800">This procedure has been archived</h3>
                <p className="text-sm text-yellow-700">
                  Archived on {formatArchiveDate(procedure.archivedOn || "")} by {archiver ? archiver.name : "Unknown"}
                  {procedure.archivedReason && (
                    <>. Reason: {procedure.archivedReason}</>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'procedure' | 'activity_log')} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full w-full sm:w-fit mb-4">
          <TabsTrigger value="procedure" className="rounded-full px-3 sm:px-4 py-2 text-sm data-[state=active]:bg-white flex-1 sm:flex-none">
            Procedure
          </TabsTrigger>
          <TabsTrigger value="activity_log" className="rounded-full px-3 sm:px-4 py-2 text-sm data-[state=active]:bg-white flex-1 sm:flex-none">
            Activity Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="procedure" className="space-y-6">
          <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Procedure Details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-3 sm:space-y-4 text-sm sm:text-base">
                  <div>
                    <dt className="text-sm text-muted-foreground">Department</dt>
                    <dd>
                      {department ? (
                        <Link
                          to={`/departments/${department.id}`}
                          className="text-foreground hover:text-primary hover:underline flex items-center gap-2"
                        >
                          <Building2 size={16} />
                          {department.departmentCode} - {department.departmentName}
                        </Link>
                      ) : "Unknown"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Owner</dt>
                    <dd className="flex items-center gap-2">
                      <UserIcon size={16} />
                      {owner ? (
                        <Link
                          to={`/people/${owner.id}`}
                          className="text-foreground hover:text-primary hover:underline"
                        >
                          {owner.name}
                        </Link>
                      ) : "Unknown"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Cadence</dt>
                    <dd className="flex items-center gap-2">
                      <Calendar size={16} />
                      {procedure.procedureCadence}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Created On</dt>
                    <dd>{toSydneyDate(procedure.createdAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Created By</dt>
                    <dd>{creator ? creator.name : "Unknown"}</dd>
                  </div>
                  {procedure.archived && (
                    <>
                      <div>
                        <dt className="text-sm text-muted-foreground">Archived On</dt>
                        <dd>{toSydneyDate(procedure.archivedOn)}</dd>
                      </div>
                      <div>
                        <dt className="text-sm text-muted-foreground">Archived By</dt>
                        <dd>{archiver ? archiver.name : "Unknown"}</dd>
                      </div>
                      {procedure.archivedReason && (
                        <div>
                          <dt className="text-sm text-muted-foreground">Archive Reason</dt>
                          <dd>{procedure.archivedReason}</dd>
                        </div>
                      )}
                    </>
                  )}
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Stakeholders</CardTitle>
                <CardDescription className="text-xs sm:text-sm">People involved in this procedure</CardDescription>
              </CardHeader>
              <CardContent>
                {stakeholders.length > 0 ? (
                  <div className="space-y-2">
                    {stakeholders.map((stakeholder: User) => (
                      <div key={stakeholder.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <UserIcon className="w-4 h-4 text-muted-foreground" />
                          <Link to={`/people/${stakeholder.id}`} className="font-semibold text-sm hover:underline">
                            {stakeholder.name}
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No stakeholders assigned</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Viewers</CardTitle>
                <CardDescription className="text-xs sm:text-sm">People with view access to this procedure</CardDescription>
              </CardHeader>
              <CardContent>
                {viewers.length > 0 ? (
                  <div className="space-y-2">
                    {viewers.map((viewer: User) => (
                      <div key={viewer.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <UserIcon className="w-4 h-4 text-muted-foreground" />
                          <Link to={`/people/${viewer.id}`} className="font-semibold text-sm hover:underline">
                            {viewer.name}
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No viewers assigned</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Description Section */}
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            {/* What Section */}
            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-4 p-4 sm:p-6">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {/* <span className="font-heading text-lg font-semibold">What</span> */}
                    <span className="text-lg font-semibold text-muted-foreground">What is this procedure</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Explain what this procedure is about in simple terms. What task or process does it cover?
                  </p>
                </div>
                {!procedure?.archived && !editingDescription && (
                  <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_DESCRIPTION}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="icon" className="flex-shrink-0" onClick={() => setEditingDescription(true)}>
                          <Edit size={16} />
                          <span className="sr-only">Edit Description</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Edit Description</p>
                      </TooltipContent>
                    </Tooltip>
                  </Can>
                )}
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                {!procedure?.archived && canEdit && editingDescription ? (
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Enter a detailed description of this procedure..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="min-h-32 resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          e.preventDefault();
                          handleDescriptionSave();
                        }
                      }}
                    />
                    <div className="flex justify-end text-sm text-muted-foreground px-1">
                      <span>Save (Ctrl + Enter)</span>
                    </div>
                    <div className="flex justify-end gap-2">
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => {
                          setEditingDescription(false);
                          setDescription(procedure?.description || "");
                        }}>
                          Cancel
                        </Button>
                        <Button onClick={handleDescriptionSave}>Save Changes</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    {procedure?.description ? (
                      <p className="whitespace-pre-wrap">{procedure.description}</p>
                    ) : (
                      <p className="text-muted-foreground italic">No description provided.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Why Section */}
            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-4 p-4 sm:p-6">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {/* <span className="font-heading text-lg font-semibold">Why</span> */}
                    <span className="text-lg font-semibold text-muted-foreground">Why is this procedure important</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Help your team understand the value. What risk does it reduce? What benefit does it bring?
                  </p>
                </div>
                {!procedure?.archived && !editingWhyImportant && (
                  <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_IMPORTANCE}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="icon" className="flex-shrink-0" onClick={() => setEditingWhyImportant(true)}>
                          <Edit size={16} />
                          <span className="sr-only">Edit Importance</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Edit Importance</p>
                      </TooltipContent>
                    </Tooltip>
                  </Can>
                )}
              </CardHeader>
              <CardContent>
                {!procedure?.archived && canEdit && editingWhyImportant ? (
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Explain why this procedure is important..."
                      value={whyImportant}
                      onChange={(e) => setWhyImportant(e.target.value)}
                      className="min-h-32 resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          e.preventDefault();
                          handleWhyImportantSave();
                        }
                      }}
                    />
                    <div className="flex justify-end text-sm text-muted-foreground px-1">
                      <span>Save (Ctrl + Enter)</span>
                    </div>
                    <div className="flex justify-end gap-2">
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => {
                          setEditingWhyImportant(false);
                          setWhyImportant(procedure?.whyImportant || "");
                        }}>
                          Cancel
                        </Button>
                        <Button onClick={handleWhyImportantSave}>Save Changes</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    {procedure?.whyImportant ? (
                      <p className="whitespace-pre-wrap">{procedure.whyImportant}</p>
                    ) : (
                      <p className="text-muted-foreground italic">No importance description provided.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 p-4 sm:p-6">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  {/* <span className="font-heading text-lg font-semibold">How</span> */}
                  <span className="text-lg font-semibold text-muted-foreground">How to complete this procedure</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Step-by-step guide to get it done. Use clear language and keep it actionable.
                </p>
              </div>
              {!procedure?.archived && (
                <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_INSTRUCTIONS}>
                  {!editingHowTo ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="icon" className="flex-shrink-0" onClick={() => setEditingHowTo(true)}>
                          <Edit size={16} />
                          <span className="sr-only">Edit</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Edit How to</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : null}
                </Can>
              )}
            </CardHeader>
            <CardContent>
              {!procedure?.archived && canEdit && editingHowTo ? (
                <div className="space-y-2">
                  <div
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        handleHowToSave();
                      }
                    }}
                  >
                    {/* <RichTextEditor
                  value={howToContent}
                  onChange={setHowToContent}
                  placeholder="Provide step-by-step instructions or important notes for completing this procedure..."
                  className="min-h-[200px]"
                  onImageUpload={handleImageUpload}
                /> */}
                    <CustomEditor
                      value={howToContent}
                      onChange={setHowToContent}
                      placeholder="Provide step-by-step instructions or important notes for completing this procedure..."
                    />
                  </div>
                  <div className="flex justify-end text-sm text-muted-foreground px-1 mt-1.5 mb-2">
                    <span>Save (Ctrl + Enter)</span>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => {
                      setEditingHowTo(false);
                      setHowToContent(procedure?.howToComplete || "");
                    }}>
                      Cancel
                    </Button>
                    <Button onClick={handleHowToSave}>Save Changes</Button>
                  </div>
                </div>
              ) : (
                <div className="prose prose-slate max-w-none dark:prose-invert">
                  {procedure?.howToComplete ? (
                    <div
                      className="ql-editor p-0 overflow-visible"
                      dangerouslySetInnerHTML={{ __html: procedure.howToComplete }}
                    />
                  ) : (
                    <p className="text-muted-foreground italic">No completion instructions provided.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            {/* Systems Used Section */}
            <Card className="h-full">
              <CardHeader className="flex flex-row items-start justify-between gap-4 p-4 sm:p-6">
                <div className="space-y-1">
                  <CardTitle className="text-lg font-semibold text-muted-foreground">Systems Used</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Add any systems used to complete this procedure.
                  </p>
                </div>
                {!procedure?.archived && !editSystems && (
                  <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_SYSTEMS}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="icon" className="flex-shrink-0" onClick={() => setEditSystems(true)}>
                          <Edit size={16} />
                          <span className="sr-only">Edit Systems</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Edit Systems</p>
                      </TooltipContent>
                    </Tooltip>
                  </Can>
                )}
              </CardHeader>
              <CardContent>
                {editSystems ? (
                  <div className="space-y-4">
                    <div className="space-y-2"
                      style={{ pointerEvents: isSavingSystems ? "none" : "inherit", opacity: isSavingSystems ? 0.5 : 1 }}
                    >
                      <Label htmlFor="systems">Select Systems</Label>
                      <MultiSelect
                        options={allSystemsFormatted}
                        selected={selectedSystems}
                        onChange={setSelectedSystems}
                        placeholder="Select systems used in this procedure"
                        maxDisplayTags={3}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" disabled={isSavingSystems} onClick={() => setEditSystems(false)}>Cancel</Button>
                      <Button onClick={handleSystemsUpdate} disabled={isSavingSystems} >{saveSystemText}</Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {procedure.systems && procedure.systems.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {!isArrayOfStrings(procedure.systems) && procedure.systems.map((system: System, index: number) => (
                          <Badge key={index} variant="secondary" className="flex items-center gap-1">
                            <Monitor size={12} className="mr-1" />
                            {system.name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground italic">No systems selected for this procedure.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Links Section */}
            <Card className="h-full">
              <CardHeader className="flex flex-row items-start justify-between gap-4 p-4 sm:p-6">
                <div className="space-y-1">
                  <CardTitle className="text-lg font-semibold text-muted-foreground">Links</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Add any link to tools, templates, or resources that make this step easier to complete.
                  </p>
                </div>
                {!procedure?.archived && (
                  <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_LINKS}>
                    <Dialog open={openAddLink} onOpenChange={setOpenAddLink}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="icon" className="flex-shrink-0">
                              <Plus size={16} />
                              <span className="sr-only">Add Link</span>
                            </Button>
                          </DialogTrigger>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Add Link</p>
                        </TooltipContent>
                      </Tooltip>
                      <DialogContent aria-describedby={undefined}>
                        <DialogHeader>
                          <DialogTitle>Add New Link</DialogTitle>
                          <DialogDescription>
                            Add a URL reference related to this procedure.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="linkTitle">Title</Label>
                            <Input
                              id="linkTitle"
                              placeholder="e.g. Company Wiki"
                              value={newLink.title}
                              onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddLink();
                                }
                              }}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="linkUrl">URL</Label>
                            <Input
                              id="linkUrl"
                              placeholder="e.g. https://example.com"
                              value={newLink.url}
                              onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddLink();
                                }
                              }}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="linkDescription">Description (Optional)</Label>
                            <Textarea
                              id="linkDescription"
                              placeholder="Add a brief description of this link..."
                              value={newLink.description}
                              onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                              className="resize-none"
                              rows={3}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleAddLink();
                                }
                              }}
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="outline">Cancel</Button>
                          </DialogClose>
                          <Button onClick={handleAddLink}>Add Link</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </Can>
                )}
              </CardHeader>
              <CardContent>
                {procedure.links && procedure.links.length > 0 ? (
                  <ul className="space-y-3">
                    {procedure.links.map((link: ProcedureLink) => (
                      <li key={link.id} className="flex items-start justify-between">
                        <div className="space-y-1">
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-primary hover:underline"
                          >
                            <Link2 size={16} />
                            <span>{link.title}</span>
                            <ExternalLink size={14} className="text-muted-foreground" />
                          </a>
                          {link.description && (
                            <p className="text-sm text-muted-foreground ml-6">{link.description}</p>
                          )}
                        </div>
                        {!procedure.archived && (
                          <div className="flex items-center gap-2">
                            <Dialog open={openEditLink && editingLink?.id === link.id} onOpenChange={(open) => {
                              setOpenEditLink(open);
                              if (open) setEditingLink(link);
                            }}>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="flex items-center gap-1">
                                  <Edit size={16} className="text-muted-foreground" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent aria-describedby={undefined}>
                                <DialogHeader>
                                  <DialogTitle>Edit Link</DialogTitle>
                                  <DialogDescription>
                                    Modify the link details.
                                  </DialogDescription>
                                </DialogHeader>
                                {editingLink && (
                                  <div className="space-y-4 py-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="editLinkTitle">Title</Label>
                                      <Input
                                        id="editLinkTitle"
                                        value={editingLink.title}
                                        onChange={(e) => setEditingLink({ ...editingLink, title: e.target.value })}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleEditLink();
                                          }
                                        }}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="editLinkUrl">URL</Label>
                                      <Input
                                        id="editLinkUrl"
                                        value={editingLink.url}
                                        onChange={(e) => setEditingLink({ ...editingLink, url: e.target.value })}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleEditLink();
                                          }
                                        }}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label htmlFor="editLinkDescription">Description (Optional)</Label>
                                      <Textarea
                                        id="editLinkDescription"
                                        placeholder="Add a brief description of this link..."
                                        value={editingLink.description || ""}
                                        onChange={(e) => setEditingLink({ ...editingLink, description: e.target.value })}
                                        className="resize-none"
                                        rows={3}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleEditLink();
                                          }
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}
                                <DialogFooter>
                                  <DialogClose asChild>
                                    <Button variant="outline">Cancel</Button>
                                  </DialogClose>
                                  <Button onClick={handleEditLink}>Save Changes</Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>

                            <Button
                              variant="ghost"
                              size="sm"
                              className="flex items-center gap-1"
                              onClick={() => handleRemoveLink(link.id)}
                            >
                              <Trash2 size={16} className="text-destructive" />
                            </Button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground italic">No links added yet.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* SharePoint Video Embed */}
          {procedure.videoEmbed && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Procedure Video</CardTitle>
              </CardHeader>
              <CardContent>
                <SharePointVideoPlayer
                  videoEmbed={procedure.videoEmbed}
                  title="Procedure Video"
                />
              </CardContent>
            </Card>
          )}

          {/* Attachments Section */}
          <div className="space-y-4">
            <FileAttachments
              resourceType="procedures"
              resourceId={procedure.id}
              attachments={procedureAttachments}
              onAttachmentsChange={handleAttachmentsChange}
              // canEdit={canEdit && !procedure.archived}
              disabled={procedure.archived}
            />
          </div>

          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold">Activities</h2>

              {!procedure.archived && (
                <Can I={ACTIONS.CREATE} a={RESOURCES.PROCEDURE_ACTIVITY}>
                  <Dialog open={openAddActivity} onOpenChange={(open) => {
                    setOpenAddActivity(open);
                    if (!open) {
                      // When the dialog closes, reset the form state.
                      setNewActivityName("");
                      setRaci({});
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button className="flex items-center gap-2 w-full sm:w-auto text-sm" size="sm">
                        <Plus size={16} />
                        <span>Add Activity</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent aria-describedby={undefined}>
                      <DialogHeader>
                        <DialogTitle>Add New Activity</DialogTitle>
                        <DialogDescription>
                          Create a new activity for this procedure.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="activityName" className="flex items-center gap-1">
                            Activity Name
                            <span className="text-black">*</span>
                          </Label>
                          <Input
                            id="activityName"
                            placeholder="Enter activity name"
                            // value={newActivity.activityName}
                            value={newActivityName}
                            // onChange={(e) => setNewActivity({...newActivity, activityName: e.target.value})}
                            onChange={(e) => setNewActivityName(e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="responsiblePerson" className="flex items-center gap-1">
                            Responsible Person (R)
                            <span className="text-black">*</span>
                          </Label>
                          <SearchableSelect
                            groups={[
                              {
                                heading: "Users",
                                options: departmentalUsers.map((user) => ({
                                  value: `User-${user.id}`,
                                  label: user.name,
                                  subtitle: user.jobRole,
                                })),
                              },
                              {
                                heading: "Job Roles",
                                options: jobRoles.map((role) => ({
                                  value: `JobRole-${role.id}`,
                                  label: `${role.title} (Role)`,
                                })),
                              },
                              {
                                heading: "External Partners",
                                options: externalPartners.map((partner: ExternalPartner) => ({
                                  value: `ExternalPartner-${partner.id}`,
                                  label: `${partner.name} (External)`,
                                })),
                              },
                            ]}
                            value={raci?.responsiblePerson ? `${raci.responsiblePerson.type}-${raci.responsiblePerson.id}` : undefined}
                            onChange={(value) => handleRaciSelect('responsiblePerson', value)}
                            placeholder="Select responsible person"
                            searchPlaceholder="Search users, roles, or partners..."
                            emptyMessage="No results found."
                            noneOption={{ value: 'user-none', label: 'None' }}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Select the person, role, or partner who will be responsible for this activity
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="accountablePerson" className="flex items-center gap-1">
                            Accountable Person (A)
                            <span className="text-black">*</span>
                          </Label>
                          <SearchableSelect
                            groups={[
                              {
                                heading: "Users",
                                options: departmentalUsers.map((user) => ({
                                  value: `User-${user.id}`,
                                  label: user.name,
                                  subtitle: user.jobRole,
                                })),
                              },
                              {
                                heading: "Job Roles",
                                options: jobRoles.map((role) => ({
                                  value: `JobRole-${role.id}`,
                                  label: `${role.title} (Role)`,
                                })),
                              },
                              {
                                heading: "External Partners",
                                options: externalPartners.map((partner: ExternalPartner) => ({
                                  value: `ExternalPartner-${partner.id}`,
                                  label: `${partner.name} (External)`,
                                })),
                              },
                            ]}
                            value={raci?.accountablePerson ? `${raci.accountablePerson.type}-${raci.accountablePerson.id}` : undefined}
                            onChange={(value) => handleRaciSelect('accountablePerson', value)}
                            placeholder="Select accountable person"
                            searchPlaceholder="Search users, roles, or partners..."
                            emptyMessage="No results found."
                            noneOption={{ value: 'user-none', label: 'None' }}
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Select the person, role, or partner who will be accountable for this activity
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="consultedPerson">Consulted Person (C)</Label>
                          <MultiSelect
                            // Assuming your MultiSelect takes these props
                            options={[
                              // Combine all selectable options here
                              ...departmentalUsers.map(user => ({ value: `User-${user.id}`, label: `${user.name} (${user.jobRole})` })),
                              ...jobRoles.map(role => ({ value: `JobRole-${role.id}`, label: `${role.title} (Role)` })),
                              ...externalPartners.map(partner => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` }))
                            ]}
                            selected={
                              // Provide an empty array as a fallback before mapping
                              (raci?.consultedPerson ?? []).map(p => `${p.type}-${p.id}`)
                            }
                            onChange={(selectedValues: string[]) => handleRaciSelect('consultedPerson', selectedValues)}
                            placeholder="Select consulted parties"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Select the person, role, or partner who should be consulted for this activity
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="informedPerson">Informed Person (I)</Label>
                          <MultiSelect
                            // Assuming your MultiSelect takes these props
                            options={[
                              // Combine all selectable options here
                              ...departmentalUsers.map(user => ({ value: `User-${user.id}`, label: `${user.name} (${user.jobRole})` })),
                              ...jobRoles.map(role => ({ value: `JobRole-${role.id}`, label: `${role.title} (Role)` })),
                              ...externalPartners.map(partner => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` }))
                            ]}
                            selected={
                              // Provide an empty array as a fallback before mapping
                              (raci?.informedPerson ?? []).map(p => `${p.type}-${p.id}`)
                            }
                            onChange={(selectedValues: string[]) => handleRaciSelect('informedPerson', selectedValues)}
                            placeholder="Select informed parties"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Select the person, role, or partner who should be informed about this activity
                          </p>
                        </div>
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button onClick={handleAddActivity}>Add Activity</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </Can>
              )}
            </div>
            <div>
              <Card>
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-base sm:text-lg">Activities</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">Tasks that make up this procedure</CardDescription>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                  {procedureActivities.length > 0 ? (
                    <>
                      <div className="hidden md:block">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Activity Name</TableHead>
                              <TableHead>Responsible (R)</TableHead>
                              <TableHead>Accountable (A)</TableHead>
                              <TableHead>Consulted (C)</TableHead>
                              <TableHead>Informed (I)</TableHead>
                              <TableHead>Created On</TableHead>
                              {canEdit && !procedure.archived && <TableHead className="text-right">Actions</TableHead>}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {procedureActivities
                              .filter((activity): activity is Activity => activity !== undefined && activity !== null && activity.id !== undefined)
                              .map((activity, index) => (
                                <TableRow key={`${activity.id}-${index}`}>
                                  <TableCell>
                                    <Link to={`/activities/${activity.id}`} className="hover:underline flex items-center">
                                      <ClipboardList size={16} className="mr-2" />
                                      {activity.activityName}
                                    </Link>
                                  </TableCell>
                                  <TableCell>
                                    {activity.raci?.responsiblePerson ? (
                                      (() => {
                                        // If it's a user, render as before
                                        const user = activity.raci?.responsiblePerson;
                                        if (user && activity.raci?.responsiblePerson?.type == PartyType.User) {
                                          return (
                                            <Link
                                              to={`/people/${user.id}`}
                                              className="hover:underline text-foreground"
                                            >
                                              {user.name}
                                            </Link>
                                          );
                                        }

                                        // Check if it's a job role
                                        const role = activity.raci?.responsiblePerson;
                                        if (role && activity.raci?.responsiblePerson?.type == PartyType.JobRole) {
                                          return (
                                            <Link
                                              to={`/job-roles/${role.id}`}
                                              className="hover:underline text-foreground"
                                            >
                                              {`${role.roleName} (Role)`}
                                            </Link>
                                          );
                                        }

                                        // Check if it's an external partner
                                        const partner = activity.raci?.responsiblePerson;
                                        if (partner && activity.raci?.responsiblePerson?.type == PartyType.ExternalPartner) {
                                          return `${partner.name} (External)`;
                                        }

                                      })()
                                    ) : "Not assigned"}
                                  </TableCell>
                                  <TableCell>
                                    {activity.raci?.accountablePerson ? (
                                      (() => {
                                        // If it's a user, render as before
                                        const user = activity.raci?.accountablePerson;
                                        if (user && activity.raci?.accountablePerson?.type == PartyType.User) {
                                          return (
                                            <Link
                                              to={`/people/${user.id}`}
                                              className="hover:underline text-foreground"
                                            >
                                              {user.name}
                                            </Link>
                                          );
                                        }

                                        // Check if it's a job role
                                        const role = activity.raci?.accountablePerson;
                                        if (role && activity.raci?.accountablePerson?.type == PartyType.JobRole) {
                                          return (
                                            <Link
                                              to={`/job-roles/${role.id}`}
                                              className="hover:underline text-foreground"
                                            >
                                              {`${role.roleName} (Role)`}
                                            </Link>
                                          );
                                        }

                                        // Check if it's an external partner
                                        const partner = activity.raci?.accountablePerson;
                                        if (partner && activity.raci?.accountablePerson?.type == PartyType.ExternalPartner) {
                                          return `${partner.name} (External)`;
                                        }
                                      })()
                                    ) : "Not assigned"}
                                  </TableCell>

                                  <TableCell>
                                    {activity.raci?.consultedPerson && activity.raci.consultedPerson.length > 0 ? (
                                      <div className="flex flex-col space-y-1">
                                        {activity.raci.consultedPerson.map((party, partyIndex) => (
                                          <div key={partyIndex}>
                                            <Link to={`/people/${party.id}`} className="hover:underline text-foreground">
                                              {getPartyName(party)}
                                            </Link>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      "None"
                                    )}
                                  </TableCell>

                                  <TableCell>
                                    {activity.raci?.informedPerson && activity.raci.informedPerson.length > 0 ? (
                                      <div className="flex flex-col space-y-1">
                                        {activity.raci.informedPerson.map((party, partyIndex) => (
                                          <div key={partyIndex}>
                                            <Link to={`/people/${party.id}`} className="hover:underline text-foreground">
                                              {getPartyName(party)}
                                            </Link>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      "None"
                                    )}
                                  </TableCell>

                                  <TableCell>{toSydneyDate(activity.createdAt)}</TableCell>
                                  {!procedure.archived && (
                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_ACTIVITY}>
                                      <TableCell className="text-right">
                                        <div className="flex justify-end gap-2">
                                          {index > 0 && (
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={() => moveActivityUp(index)}
                                              className="text-gray-600"
                                            >
                                              <ArrowUp size={16} />
                                            </Button>
                                          )}
                                          {index < procedureActivities.length - 1 && (
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={() => moveActivityDown(index)}
                                              className="text-gray-600"
                                            >
                                              <ArrowDown size={16} />
                                            </Button>
                                          )}
                                          <Dialog open={openEditActivity && editingActivity?.id === activity.id} onOpenChange={(open) => {
                                            setOpenEditActivity(open);
                                            if (open) {
                                              setEditingActivity({
                                                id: activity.id,
                                                activityName: activity.activityName,
                                                raci: activity.raci
                                              });
                                            }
                                          }}>
                                            <DialogTrigger asChild>
                                              <Button variant="outline" size="icon">
                                                <Edit size={16} />
                                              </Button>
                                            </DialogTrigger>
                                            <DialogContent aria-describedby={undefined}>
                                              <DialogHeader>
                                                <DialogTitle>Edit Activity</DialogTitle>
                                              </DialogHeader>
                                              {editingActivity && (
                                                <div className="space-y-4 py-4">
                                                  <div className="space-y-2">
                                                    <Label htmlFor="activityName">Activity Name</Label>
                                                    <Input
                                                      id="activityName"
                                                      value={editingActivity.activityName}
                                                      onChange={(e) => setEditingActivity({ ...editingActivity, activityName: e.target.value })}
                                                    />
                                                  </div>

                                                  <div className="space-y-2">
                                                    <Label htmlFor="responsiblePerson" className="flex items-center gap-1">
                                                      Responsible Person (R)
                                                      <span className="text-black">*</span>
                                                    </Label>
                                                    <SearchableSelect
                                                      groups={[
                                                        {
                                                          heading: "Users",
                                                          options: departmentalUsers.map((user) => ({
                                                            value: `User-${user.id}`,
                                                            label: user.name,
                                                            subtitle: user.jobRole,
                                                          })),
                                                        },
                                                        {
                                                          heading: "Job Roles",
                                                          options: jobRoles.map((role) => ({
                                                            value: `JobRole-${role.id}`,
                                                            label: `${role.title} (Role)`,
                                                          })),
                                                        },
                                                        {
                                                          heading: "External Partners",
                                                          options: externalPartners.map((partner: ExternalPartner) => ({
                                                            value: `ExternalPartner-${partner.id}`,
                                                            label: `${partner.name} (External)`,
                                                          })),
                                                        },
                                                      ]}
                                                      value={raci?.responsiblePerson ? `${raci.responsiblePerson.type}-${raci.responsiblePerson.id}` : undefined}
                                                      onChange={(value) => handleRaciSelect('responsiblePerson', value)}
                                                      placeholder="Select responsible person"
                                                      searchPlaceholder="Search users, roles, or partners..."
                                                      emptyMessage="No results found."
                                                      noneOption={{ value: 'user-none', label: 'None' }}
                                                    />
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                      Select the person, role, or partner who will be responsible for this activity
                                                    </p>
                                                  </div>

                                                  <div className="space-y-2">
                                                    <Label htmlFor="accountablePerson" className="flex items-center gap-1">
                                                      Accountable Person (A)
                                                      <span className="text-black">*</span>
                                                    </Label>
                                                    <SearchableSelect
                                                      groups={[
                                                        {
                                                          heading: "Users",
                                                          options: departmentalUsers.map((user) => ({
                                                            value: `User-${user.id}`,
                                                            label: user.name,
                                                            subtitle: user.jobRole,
                                                          })),
                                                        },
                                                        {
                                                          heading: "Job Roles",
                                                          options: jobRoles.map((role) => ({
                                                            value: `JobRole-${role.id}`,
                                                            label: `${role.title} (Role)`,
                                                          })),
                                                        },
                                                        {
                                                          heading: "External Partners",
                                                          options: externalPartners.map((partner: ExternalPartner) => ({
                                                            value: `ExternalPartner-${partner.id}`,
                                                            label: `${partner.name} (External)`,
                                                          })),
                                                        },
                                                      ]}
                                                      value={raci?.accountablePerson ? `${raci.accountablePerson.type}-${raci.accountablePerson.id}` : undefined}
                                                      onChange={(value) => handleRaciSelect('accountablePerson', value)}
                                                      placeholder="Select accountable person"
                                                      searchPlaceholder="Search users, roles, or partners..."
                                                      emptyMessage="No results found."
                                                      noneOption={{ value: 'user-none', label: 'None' }}
                                                    />
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                      Select the person, role, or partner who will be accountable for this activity
                                                    </p>
                                                  </div>

                                                  <div className="space-y-2">
                                                    <Label htmlFor="consultedPerson">Consulted Person (C)</Label>
                                                    <MultiSelect
                                                      // Assuming your MultiSelect takes these props
                                                      options={[
                                                        // Combine all selectable options here
                                                        ...departmentalUsers.map(user => ({ value: `User-${user.id}`, label: `${user.name} (${user.jobRole})` })),
                                                        ...jobRoles.map(role => ({ value: `JobRole-${role.id}`, label: `${role.title} (Role)` })),
                                                        ...externalPartners.map(partner => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` }))
                                                      ]}
                                                      selected={
                                                        // Provide an empty array as a fallback before mapping
                                                        (raci?.consultedPerson ?? []).map(p => `${p.type}-${p.id}`)
                                                      }
                                                      onChange={(selectedValues: string[]) => handleRaciSelect('consultedPerson', selectedValues)}
                                                      placeholder="Select consulted parties"
                                                    />
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                      Select the person, role, or partner who should be consulted for this activity
                                                    </p>
                                                  </div>

                                                  <div className="space-y-2">
                                                    <Label htmlFor="informedPerson">Informed Person (I)</Label>
                                                    <MultiSelect
                                                      // Assuming your MultiSelect takes these props
                                                      options={[
                                                        // Combine all selectable options here
                                                        ...departmentalUsers.map(user => ({ value: `User-${user.id}`, label: `${user.name} (${user.jobRole})` })),
                                                        ...jobRoles.map(role => ({ value: `JobRole-${role.id}`, label: `${role.title} (Role)` })),
                                                        ...externalPartners.map(partner => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` }))
                                                      ]}
                                                      selected={
                                                        // Provide an empty array as a fallback before mapping
                                                        (raci?.informedPerson ?? []).map(p => `${p.type}-${p.id}`)
                                                      }
                                                      onChange={(selectedValues: string[]) => handleRaciSelect('informedPerson', selectedValues)}
                                                      placeholder="Select informed parties"
                                                    />
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                      Select the person, role, or partner who should be informed about this activity
                                                    </p>
                                                  </div>
                                                </div>
                                              )}
                                              <DialogFooter>
                                                <DialogClose asChild>
                                                  <Button variant="outline">Cancel</Button>
                                                </DialogClose>
                                                <Button onClick={handleUpdateActivity}>Save Changes</Button>
                                              </DialogFooter>
                                            </DialogContent>
                                          </Dialog>

                                          {canDelete && (
                                            <Dialog open={openDeleteActivity && deleteActivityId === activity.id} onOpenChange={(open) => {
                                              setOpenDeleteActivity(open);
                                              if (open) setDeleteActivityId(activity.id);
                                            }}>
                                              <DialogTrigger asChild>
                                                <Button variant="outline" size="icon" className="text-destructive">
                                                  <Trash2 size={16} />
                                                </Button>
                                              </DialogTrigger>
                                              <DialogContent aria-describedby={undefined}>
                                                <DialogHeader>
                                                  <DialogTitle>Delete Activity</DialogTitle>
                                                </DialogHeader>
                                                <div className="py-4">
                                                  <p>Are you sure you want to delete the activity "{activity.activityName}"?</p>
                                                  <p className="text-sm text-muted-foreground mt-2">This action cannot be undone and will delete all associated training records.</p>
                                                </div>
                                                {!isDeletingActivity ? (
                                                  <DialogFooter>
                                                    <DialogClose asChild>
                                                      <Button variant="outline">Cancel</Button>
                                                    </DialogClose>
                                                    <Button variant="destructive" onClick={handleDeleteActivity}>Delete</Button>
                                                  </DialogFooter>
                                                ) : (
                                                  <DialogFooter>
                                                    <Button variant="destructive" disabled={true}>Deleting...</Button>
                                                  </DialogFooter>
                                                )}
                                              </DialogContent>
                                            </Dialog>
                                          )
                                          }
                                        </div>
                                      </TableCell>
                                    </Can>
                                  )}
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </div>

                      <div className="md:hidden space-y-3 p-4">
                        {procedureActivities
                          .filter((activity): activity is Activity => activity !== undefined && activity !== null && activity.id !== undefined)
                          .map((activity, index) => (
                            <Card key={`${activity.id}-${index}`} className="border">
                              <CardContent className="p-4 space-y-3">
                                {/* Activity Name */}
                                <div className="flex items-start justify-between gap-2">
                                  <Link to={`/activities/${activity.id}`} className="hover:underline flex items-center gap-2 font-medium text-sm flex-1 min-w-0">
                                    <ClipboardList size={16} className="flex-shrink-0" />
                                    <span className="break-words">{activity.activityName}</span>
                                  </Link>
                                  {!procedure.archived && (
                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_ACTIVITY}>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                                            <MoreVertical size={16} />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          {index > 0 && (
                                            <DropdownMenuItem onClick={() => moveActivityUp(index)}>
                                              <ArrowUp className="w-4 h-4 mr-2" />
                                              Move Up
                                            </DropdownMenuItem>
                                          )}
                                          {index < procedureActivities.length - 1 && (
                                            <DropdownMenuItem onClick={() => moveActivityDown(index)}>
                                              <ArrowDown className="w-4 h-4 mr-2" />
                                              Move Down
                                            </DropdownMenuItem>
                                          )}
                                          <DropdownMenuItem onClick={() => {
                                            setEditingActivity({
                                              id: activity.id,
                                              activityName: activity.activityName,
                                              raci: activity.raci
                                            });
                                            setOpenEditActivity(true);
                                          }}>
                                            <Edit className="w-4 h-4 mr-2" />
                                            Edit
                                          </DropdownMenuItem>
                                          {canDelete && (
                                            <DropdownMenuItem
                                              onClick={() => {
                                                setDeleteActivityId(activity.id);
                                                setOpenDeleteActivity(true);
                                              }}
                                              className="text-red-600"
                                            >
                                              <Trash2 className="w-4 h-4 mr-2" />
                                              Delete
                                            </DropdownMenuItem>
                                          )}
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </Can>
                                  )}
                                </div>

                                {/* RACI Information */}
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="text-muted-foreground block mb-1">Responsible</span>
                                    <span className="font-medium break-words">
                                      {activity.raci?.responsiblePerson ? getPartyName(activity.raci.responsiblePerson) : "Not assigned"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground block mb-1">Accountable</span>
                                    <span className="font-medium break-words">
                                      {activity.raci?.accountablePerson ? getPartyName(activity.raci.accountablePerson) : "Not assigned"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground block mb-1">Consulted</span>
                                    <span className="font-medium break-words">
                                      {activity.raci?.consultedPerson && activity.raci.consultedPerson.length > 0
                                        ? activity.raci.consultedPerson.map(p => getPartyName(p)).join(", ")
                                        : "None"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground block mb-1">Informed</span>
                                    <span className="font-medium break-words">
                                      {activity.raci?.informedPerson && activity.raci.informedPerson.length > 0
                                        ? activity.raci.informedPerson.map(p => getPartyName(p)).join(", ")
                                        : "None"}
                                    </span>
                                  </div>
                                </div>

                                {/* Created Date */}
                                <div className="text-xs text-muted-foreground">
                                  Created: {activity.createdAt ? new Date(activity.createdAt).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  }) : ""}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-muted-foreground">No activities created yet.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="activity_log">
          <ActivityLog logs={activityLogs || []} entityName="Procedure" users={users} currentUser={currentUser} />
        </TabsContent>
      </Tabs>

      <ProcedureQRModal
        isOpen={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        procedure={procedure}
      />
    </div>
  );
}
